import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, take } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class UserService {
  private userSubject = new BehaviorSubject<any>(null);
  private user$ = this.userSubject.asObservable();

  storedUser: string;

  constructor() {
    this.storedUser = localStorage.getItem('userdata') || '';
    if (this.storedUser) this.userSubject.next(JSON.parse(this.storedUser));
    else this.userSubject.next('');
  }

  setUser(user: any) {
    if (user?.agency?.balances?.length) {
      let balance = this.truncateTo2Digits(user.agency.balances[0].amount);
      user.agency.balances[0].amount = balance;
    }

    localStorage.setItem('userdata', JSON.stringify(user));
    this.storedUser = JSON.stringify(user);
    this.userSubject.next(user);
  }

  getUser(): Observable<any> {
    return this.user$;
  }

  getHeadAgencyId(): string {
    if (!this.storedUser) return '';

    const data = JSON.parse(this.storedUser);

    // If current agency is a head/root agency, use its own agency ID
    if (data?.agency?.headAgencyFlag === true || data?.rootAgent === true) {
      return data?.agencyId || data?.agency?.id || '';
    }

    // If current agency is a sub-agency, use its parent head agency ID
    return data?.agency?.headAgency || '';
  }
  updateBalance(newBalance: number, id?: string) {
    this.getUser()
      .pipe(take(1))
      .subscribe({
        next: (userData) => {
          let balance: any;

          if (!id) {
            balance = userData?.agency?.balances?.[0];
          } else {
            balance = (userData?.agency?.balances as [])?.find(
              (b: any) => b?.id == id,
            );
          }

          if (balance) {
            balance.amount = this.truncateTo2Digits(newBalance);
            this.setUser(userData);
          }
        },
      });
  }

  private truncateTo2Digits(num: number): number {
    return Math.trunc(num * 100) / 100;
  }

  getAgencyId(): string {
    return this.storedUser ? JSON.parse(this.storedUser).agencyId : '';
  }

  getAgencyRole(): string {
    return this.storedUser ? JSON.parse(this.storedUser).role : '';
  }

  /**
   * True se i dati agenzia (`userdata`) sono presenti nel localStorage.
   * Letto direttamente dallo storage, non da {@link storedUser}, perché
   * {@link clearUser} rimuove la chiave senza azzerare il campo.
   */
  hasUserData(): boolean {
    return !!localStorage.getItem('userdata');
  }

  /**
   * True se l'utente loggato appartiene a una head agency (root agency).
   * Usato dal {@code rootAgencyManagerGuard} per filtrare le pagine di gestione
   * margin (visibili solo ai manager root).
   *
   * NOTA: il BE serializza il response con due campi quasi omonimi:
   *   - agency.headAgencyFlag : boolean — il flag root vero/proprio
   *   - agency.headAgency     : string  — l'UUID dell'agency parent (o null se root)
   * In aggiunta, AgentResponse.rootAgent è il boolean a livello root del payload.
   * Leggiamo il primo disponibile, con fallback per retrocompat.
   */
  isHeadAgency(): boolean {
    if (!this.storedUser) return false;
    const data = JSON.parse(this.storedUser);
    if (typeof data?.agency?.headAgencyFlag === 'boolean')
      return data.agency.headAgencyFlag;
    if (typeof data?.rootAgent === 'boolean') return data.rootAgent;
    // Fallback legacy: se headAgency è esplicitamente boolean true; se è string (UUID parent) NON è root.
    return data?.agency?.headAgency === true;
  }

  /**
   * True se l'utente loggato è un manager (ADMIN o AGENCY_MANAGER) della
   * root agency. Combina role check + head agency check.
   */
  isRootAgencyManager(): boolean {
    const role = this.getAgencyRole();
    const isManager = role === 'ADMIN' || role === 'AGENCY_MANAGER';
    return isManager && this.isHeadAgency();
  }

  getAgencyDefaultCurrencyAbbreviation() {
    return this.storedUser
      ? JSON.parse(this.storedUser).defaultCurrency?.abbreviation
      : '';
  }

  getAgencyBalance(): { balance: 0; abbreviation: '' } {
    return this.storedUser
      ? {
          balance: JSON.parse(this.storedUser).agency?.balances?.[0]?.amount,
          abbreviation: JSON.parse(this.storedUser).agency?.balances?.[0]
            ?.currency?.abbreviation,
        }
      : { balance: 0, abbreviation: '' };
  }

  ///////////////////////////////////////////////////////////////////////////////////////

  getSubAgencyBalanceDetails(): {
    id: string;
    balance: number;
    abbreviation: string;
  } {
    let balanceDetails = sessionStorage.getItem('agencybalancesdetails');
    return balanceDetails
      ? JSON.parse(balanceDetails)
      : { id: '', balance: 0, abbreviation: 'EUR' };
  }

  setSubAgencyBalanceDetails(balanceDetails: any) {
    sessionStorage.setItem(
      'agencybalancesdetails',
      JSON.stringify(balanceDetails),
    );
  }

  clearUser() {
    localStorage.removeItem('userdata');
    this.userSubject.next(null);
  }
}
